/**
 * Copyright (c) 2025 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useRootSelector } from '../../../hooks/useRootSelector';
import { addNotifications } from '../../../actions/pageActions';
import { useSoundEffect } from '../../../hooks/useSoundEffect';
import admitNotificationSound from '../../../assets/sounds/admitNotification.mp3';
import { CommonAPI } from '../../../api/common/responses';

type ReadyUser = CommonAPI.User & { participantSessionId: string };

const getUserIds = (users: ReadyUser[]) => users.map(user => user.participantSessionId);

export const useAdmissionNotifications = (isNotary: boolean) => {
  const dispatch = useDispatch();
  const { signingRoom } = useRootSelector(state => state);
  const { play: playAdmitSound } = useSoundEffect({
    soundFile: admitNotificationSound,
  });

  // Keep track of users who were previously ready to be admitted
  const previousReadyUsersRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isNotary) {
      return;
    }

    // Get current ready users with valid session IDs
    const currentReadyUsers = signingRoom.users.onboarding
      .filter(session => session.showAdmitButton)
      .flatMap(session => session.users)
      .filter((user): user is ReadyUser => !!user.participantSessionId);

    // Find users that just became ready to be admitted
    const newlyReadyUsers = currentReadyUsers.filter(
      user => !previousReadyUsersRef.current.has(user.participantSessionId),
    );

    if (newlyReadyUsers.length > 0) {
      // Play sound and show notification for each newly ready user
      playAdmitSound();
      addNotifications(
        newlyReadyUsers.map(user => {
          const fullName = `${user.firstName} ${user.lastName}`;
          return {
            type: 'info',
            iconType: 'info',
            content: `${fullName} is ready to be admitted`,
            shouldAutoRemove: true,
          };
        }),
      )(dispatch);
    }

    // Update the ref with current ready users
    previousReadyUsersRef.current = new Set(getUserIds(currentReadyUsers));
  }, [signingRoom.users.onboarding, isNotary, playAdmitSound]);
};
